import React, { useEffect, useState } from "react";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { FaPlus, FaTimes, FaEdit, FaTrash, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [isOtherCircle, setIsOtherCircle] = useState(false);
  const [form, setForm] = useState({
    name: "",
    username: "",
    password: "",
    circle: "",
    role: "inspector",
    rank: "Inspector",
    flag_verification_type: "review_cv",
  });
  const [editingUserId, setEditingUserId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [rankFilter, setRankFilter] = useState("all"); // rank filter state
  const [visiblePasswords, setVisiblePasswords] = useState({}); // track visible passwords by user id

  const togglePasswordVisibility = (userId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get("/users");
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const fetchCircles = async () => {
    try {
      const res = await axios.get("/properties/circles");
      setCircles(res.data || []);
    } catch (err) {
      console.error("Failed to fetch circles", err);
    }
  };

  const navigate = useNavigate();
  useEffect(() => {
    fetchUsers();
    fetchCircles();
  }, []);

  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCircleSelect = (e) => {
    const value = e.target.value;
    if (value === "__other__") {
      setIsOtherCircle(true);
      setForm({ ...form, circle: "" });
    } else {
      setIsOtherCircle(false);
      setForm({ ...form, circle: value });
    }
  };

  const normalizeFlagType = (user) => {
    const existing = String(user?.flag_verification_type || "").trim().toLowerCase();
    if (
      existing === "review_cv" ||
      existing === "review_supervisor" ||
      existing === "add_cv" ||
      existing === "add_supervisor"
    ) {
      return existing;
    }
    if (existing === "ai") return "review_cv";
    if (existing === "supervisor") return "review_supervisor";

    const username = String(user?.username || "").trim().toLowerCase();
    const rank = Number(user?.rank);

    if (username === "inspector1" || rank === 4 || rank === 7) return "add_cv";
    if (username === "inspector2" || rank === 1 || rank === 8) return "add_supervisor";
    if (username === "inspector6" || rank === 6) return "review_cv";
    if (username === "inspector9" || rank === 9 || rank === 2 || rank === 3 || rank === 5) return "review_supervisor";

    return "review_cv";
  };

  const openForm = (user = null) => {
    if (user) {
      setForm({
        name: user.name,
        username: user.username,
        password: "",
        circle: user.circle,
        role: user.role,
        rank: user.rank,
        flag_verification_type: normalizeFlagType(user),
      });
      setIsOtherCircle(!!user.circle && !circles.includes(user.circle));
      setEditingUserId(user.id);
    } else {
      setForm({
        name: "",
        username: "",
        password: "",
        circle: "",
        role: "inspector",
        rank: "Inspector",
        flag_verification_type: "review_cv",
      });
      setIsOtherCircle(false);
      setEditingUserId(null);
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || (!editingUserId && !form.password) || !form.role)
      return;

    try {
      if (editingUserId) {
        await axios.put(`/users/${editingUserId}`, form);
      } else {
        await axios.post("/users", form);
      }
      fetchUsers();
      closeForm();
    } catch (err) {
      console.error("Submit error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this user?")) {
      try {
        await axios.delete(`/users/${id}`);
        fetchUsers();
      } catch (err) {
        console.error("Delete error:", err);
      }
    }
  };

  // ✅ Apply rank filter before grouping
  const filteredUsers =
    rankFilter === "all"
      ? users
      : users.filter((user) => user.rank === rankFilter);

  // ✅ Group users by role
  const groupedUsers = filteredUsers.reduce((groups, user) => {
    if (!groups[user.role]) groups[user.role] = [];
    groups[user.role].push(user);
    return groups;
  }, {});

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 bg-gray-50 p-6 overflow-y-auto relative">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h1 className="text-3xl font-bold text-teal-700">
            User Management
          </h1>

          <div className="flex items-center gap-3">
            {/* ✅ Rank Filter Dropdown */}
            <select
              value={rankFilter}
              onChange={(e) => setRankFilter(e.target.value)}
              className="border p-2 rounded-lg shadow-sm focus:ring-2 focus:ring-teal-400"
            >
              <option value="all">All Ranks</option>
              <option value="Inspector">Inspector</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Superadmin">Superadmin</option>
            </select>

            <button
              onClick={() => openForm()}
              className="flex items-center bg-teal-600 text-white px-4 py-2 rounded-lg shadow hover:bg-teal-700 transition"
            >
              <FaPlus className="mr-2" /> Add User
            </button>
          </div>
        </div>
        {/* Floating Form */}
        {showForm && (
          <div className="absolute top-6 right-6 bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-sm p-6 z-40 animate-fade-in">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-teal-700">
                {editingUserId ? "Edit User" : "Add User"}
              </h2>
              <button
                onClick={closeForm}
                className="text-gray-400 hover:text-red-500"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleInput}
                  placeholder="Full Name"
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Username</label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleInput}
                  placeholder="Username"
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Circle</label>
                <select
                  value={isOtherCircle ? "__other__" : form.circle}
                  onChange={handleCircleSelect}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                  required={!isOtherCircle}
                >
                  <option value="">Select Circle</option>
                  {circles.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__other__">Other (type new circle)</option>
                </select>
                {isOtherCircle && (
                  <input
                    type="text"
                    name="circle"
                    value={form.circle}
                    onChange={handleInput}
                    placeholder="Enter new circle name"
                    className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400 mt-2"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {editingUserId ? "Update Password (optional)" : "Password"}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleInput}
                  placeholder={
                    editingUserId ? "Update Password (optional)" : "Password"
                  }
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                  required={!editingUserId}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleInput}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                >
                  <option value="supervisor">Supervisor</option>
                  <option value="inspector">Inspector</option>
                </select>
              </div>

              {/* <select
                name="rank"
                value={form.rank}
                onChange={handleInput}
                className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
              >
                <option value="Inspector">Inspector</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Superadmin">Superadmin</option>
              </select> */}

              {form.role === "inspector" && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Inspector Workflow Type</label>
                  <select
                    name="flag_verification_type"
                    value={form.flag_verification_type || "review_cv"}
                    onChange={handleInput}
                    className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-teal-400"
                    required
                  >
                    <option value="review_cv">(i) Flagged for Verification by Computer Vision</option>
                    <option value="review_supervisor">(ii) Flagged for Verification by Supervisor</option>
                    <option value="add_cv">(iii) S1D - Flagged by CV</option>
                    <option value="add_supervisor">(iv) S1D - Flagged by Supervisor</option>
                  </select>
                </div>
              )}

              <button
                type="submit"
                className="bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 transition"
              >
                {editingUserId ? "Update User" : "Add User"}
              </button>
            </form>
          </div>
        )}

        {/* Grouped Tables */}
        {Object.keys(groupedUsers).length === 0 ? (
          <p className="text-center text-gray-500 mt-10">No users found.</p>
        ) : (
          <div className="space-y-10">
            {Object.entries(groupedUsers).map(([role, roleUsers]) => (
              <div
                key={role}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="bg-teal-600 text-white px-4 py-3 text-lg font-semibold capitalize">
                  {role} ({roleUsers.length})
                </div>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left">#</th>
                      <th className="px-4 py-2 text-left">Username</th>
                      <th className="px-4 py-2 text-left">Password</th>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Rank</th>
                      <th className="px-4 py-2 text-left">Circle</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        className="border-b hover:bg-teal-50 transition"

                      >
                        <td className="px-4 py-2 font-semibold text-gray-700">
                          {index + 1}
                        </td>
                        <td
                          onClick={() => navigate(`/superadmin/Users/${user.id}`)}
                          className="px-6 py-3 cursor-pointer border-b border-gray-200 
             text-sm font-semibold text-gray-800 
             hover:text-sky-600 hover:bg-sky-50 
             transition-colors duration-200 relative group"
                        >
                          <span className="relative z-10">{user.username}</span>
                          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-sky-500 transition-all duration-300 group-hover:w-full"></span>
                        </td>

                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-gray-600">
                              {visiblePasswords[user.id] ? user.plain_password || "N/A" : "••••••••"}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePasswordVisibility(user.id);
                              }}
                              className="text-gray-400 hover:text-teal-600 focus:outline-none"
                              title={visiblePasswords[user.id] ? "Hide Password" : "Show Password"}
                            >
                              {visiblePasswords[user.id] ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                        </td>


                        <td className="px-4 py-2">{user.name}</td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-1 text-xs rounded bg-teal-100 text-teal-700">
                            {user.role === 'inspector' && !isNaN(Number(user.rank))
                              ? 'Inspector'
                              : user.role === 'supervisor' && !isNaN(Number(user.rank))
                                ? 'Supervisor'
                                : user.rank}
                          </span>
                        </td>
                        <td className="px-4 py-2">{user.circle}</td>
                        <td className="px-4 py-2 text-right space-x-2">
                          <button
                            onClick={() => openForm(user)}
                            className="text-yellow-500 hover:text-yellow-600"
                          >
                            <FaEdit />
                          </button>
                          {String(user.role).toLowerCase() !== "superadmin" && (
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default UserManagement;
